import { FirebaseError } from 'firebase/app'
import * as Sentry from '@sentry/browser'
import { i18n } from '@/libraries/i18n'

const MESSAGES: Record<string, () => string> = {
  'auth/account-exists-with-different-credential': () =>
    i18n.t('authentication.account_exists_with_different_credential'),
  'auth/email-already-in-use': () => i18n.t('authentication.email_in_use'),
  // Firebase answers with this for both a wrong address and a wrong password once email enumeration protection
  // is on, so it cannot say which of the two is wrong.
  'auth/invalid-credential': () => i18n.t('authentication.wrong_email_or_password'),
  'auth/invalid-email': () => i18n.t('authentication.invalid_email'),
  'auth/missing-password': () => i18n.t('authentication.missing_password'),
  'auth/network-request-failed': () => i18n.t('authentication.network_request_failed'),
  'auth/password-does-not-meet-requirements': () => i18n.t('authentication.weak_password'),
  'auth/popup-blocked': () => i18n.t('authentication.popup_blocked'),
  'auth/too-many-requests': () => i18n.t('authentication.too_many_requests'),
  'auth/user-disabled': () => i18n.t('authentication.user_disabled'),
  'auth/user-not-found': () => i18n.t('authentication.user_not_found'),
  'auth/weak-password': () => i18n.t('authentication.weak_password'),
  'auth/wrong-password': () => i18n.t('authentication.wrong_password'),
}

// The user closed the Google popup, or opened a second one, so they already know.
const SILENT_CODES = ['auth/popup-closed-by-user', 'auth/cancelled-popup-request']

// The sign-up page signs in an address that is already in use, so a wrong password means someone else has it.
const WRONG_CREDENTIAL_CODES = ['auth/invalid-credential', 'auth/wrong-password']

// The translated message for an error from signing in to Firebase, or null when there is nothing to tell the user.
// Any other error is reported, since Firebase's own message is English and names the error code.
export function authErrorMessage(error: unknown, { registering = false } = {}): string | null {
  if (error instanceof FirebaseError) {
    if (SILENT_CODES.includes(error.code)) return null
    if (registering && WRONG_CREDENTIAL_CODES.includes(error.code)) return i18n.t('authentication.email_in_use')

    const message = MESSAGES[error.code]
    if (message !== undefined) return message()
  }

  Sentry.captureException(error)
  return i18n.t('authentication.unexpected_error')
}
